import { useMemo } from 'react';
import type { UIMatch } from 'react-router';
import { useMatches, useRouteLoaderData, useSearchParams } from 'react-router';
import { useAtomValue } from 'jotai';

import { previewPatchAtom } from '@modules/business-manager/state/preview-patch';

import {
    PREVIEW_BASE_ROUTE_ID,
    PROFESSIONAL_DETAILS_BASE_ROUTE_ID,
} from '@modules/business-manager/config/constants';
import type { APHandle } from '~/modules/root';
import type { loader as layoutLoader } from '@modules/professional/page/loaders/professional-page-loader.server';

type ProfessionalLayoutData = Awaited<ReturnType<typeof layoutLoader>>['data'];

/**
 * The persistent professional layout's payload, from whichever layout is
 * actually mounted.
 *
 * A professional page renders under two different parents: the public route
 * and the Business Manager preview iframe. They carry the same data but
 * different route ids, and `useRouteLoaderData` matches on the id — so asking
 * for the public one by name returns undefined inside the preview, and the
 * revamp's shell components silently render nothing. Always resolve through
 * here rather than naming a route id at the call site.
 */
export const useProfessionalLayoutData = ():
    | ProfessionalLayoutData
    | undefined => {
    const publicData = useRouteLoaderData<typeof layoutLoader>(
        PROFESSIONAL_DETAILS_BASE_ROUTE_ID,
    );
    const previewData = useRouteLoaderData<typeof layoutLoader>(
        PREVIEW_BASE_ROUTE_ID,
    );
    const patch = useAtomValue(previewPatchAtom);

    const data = publicData ?? previewData;

    // Business Manager edits arrive here as field-level overrides so the preview
    // can repaint in the next frame instead of re-running the route's loaders.
    // Every consumer of the layout payload reads through this hook, so applying
    // them once here covers the hero, the profile header and the about band
    // without any of them knowing a patch exists. Empty on the public site.
    return useMemo(() => {
        if (!data || !patch.initial) return data;
        return { ...data, initial: { ...data.initial, ...patch.initial } };
    }, [data, patch]);
};

/**
 * Whether the editorial revamp (BC-4505) should render.
 *
 * The flag lives on the layout loader so every tab agrees; `?revamp=1` (or
 * `?detailsRevamp=1`) forces it in local dev only. Every route that can serve
 * both a legacy and a revamped page asks this, so the two never disagree —
 * which matters most in the preview, where the layout shell and the page body
 * are decided separately.
 */
export const useShowProfessionalRevamp = (): boolean => {
    const layoutData = useProfessionalLayoutData();
    const [searchParams] = useSearchParams();

    return Boolean(
        layoutData?.detailsRevamp ||
        (import.meta.env.DEV &&
            (searchParams.get('revamp') === '1' ||
                searchParams.get('detailsRevamp') === '1')),
    );
};

/**
 * Whether the persistent layout should render the revamp's chrome.
 *
 * The flag alone is not enough: tabs the revamp never took over (room spaces,
 * the nested team-member and testimonial pages) still serve their legacy body,
 * and wrapping those in the revamp header would pair a magazine hero with a
 * legacy page. A route opts in by setting `professionalRevampLayout` (or
 * `isOverviewPage`) on its handle.
 *
 * Both the public layout and the Business Manager preview ask this, so the
 * chrome and the body can never disagree about which era they are in.
 */
export const useProfessionalRevampLayoutActive = (): boolean => {
    const matches = useMatches();
    const revampActive = useShowProfessionalRevamp();

    const ownsRevampLayout = matches.some((match) => {
        const handle = (match as UIMatch<unknown, APHandle>)?.handle;
        return (
            handle?.isOverviewPage === true ||
            handle?.professionalRevampLayout === true
        );
    });

    return ownsRevampLayout && revampActive;
};
